import { useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { Product } from '../api/useProducts.ts';
import type { Program, ProgramCreate, ProgramUpdate } from '../api/usePrograms.ts';
import { Varsel } from '../hypoteser/deler.tsx';
import { Knapp } from '../innboks/Detalj.tsx';
import { Ikon } from '../status/Ikon.tsx';
import { Om } from '../skall/Om.tsx';
import { useSmal } from '../skall/useSmalSkjerm.ts';
import type { UtfyltFelt, Utfylling } from './fraHypotese.ts';
import { periode } from './periode.ts';

/** Kontraktens tak: ProgramCreate.name, .hypothesis, SuccessCriterion.text og .target. */
export const NAVN_MAKS = 80;
export const HYPOTESE_MAKS = 280;
export const KRITERIUM_MAKS = 280;
export const MALTALL_MAKS = 80;
export const MAKS_OPPGAVER = 5;

/** DD-33: oppgave 1 er den samme i alle program — den lærer testeren flyten før den trengs. Kan fjernes. */
export const STANDARD_OPPGAVE = {
  title: 'Send ditt første innspill',
  instruction: 'Åpne innspillsknappen i appen og send et innspill — hva som helst. Da kjenner du flyten når du trenger den.',
  screenHint: '',
  estimatedMinutes: '5',
};

interface Oppgaverad {
  title: string;
  instruction: string;
  screenHint: string;
  estimatedMinutes: string;
}

export type ProgramSkjemaModus = { navn: 'ny' } | { navn: 'rediger'; program: Program };

/**
 * Skjemaet åpnet fra hypotesen (H3.4, «Lag betaprogram fra denne …»): forhåndsutfylt i klienten,
 * med merke på hvert felt som kom fra hypotesen til hun endrer det. Ingenting finnes på tjeneren
 * før «Lagre utkast».
 */
export interface FraHypotese {
  nr: number;
  tittel: string;
  utfylling: Utfylling;
  /** Hypotesen hun kom fra — «‹ H-142» og «Lukk» går dit, med fokus på døren. */
  tilbake: { til: string; state: unknown };
  /** Svaret fra tjeneren på siste lagring, i klientens ord (skjerm 20). Teksten i feltene står. */
  feil?: { tekst: string; onProevIgjen?: () => void } | null;
}

/**
 * Nytt program (4a). Én skjerm i rapportens rekkefølge: hva testes, hypotese,
 * suksesskriterium, periode, kontakttak, vilkår, oppgaver med anslått tid.
 *
 * To knapper, to betydninger: «Lagre utkast» kan alltid brukes, «Åpne for invitasjoner»
 * er låst til hypotese, suksesskriterium og minst én oppgave med tid finnes — og låsen
 * FORKLARES (DD-17). `aktiv` settes aldri herfra; den følger leietakerens første
 * flaggbekreftelse (M3.3).
 *
 * Forpliktelsesfri form ved `minorsPresent` (K37): vilkårsfeltet er deaktivert med
 * grunnen synlig — ikke tomt, låst (DD-37).
 */
export function Skjema({
  modus,
  produkter,
  produkterTilstand,
  maksKontakterTak,
  minorsPresent,
  innstillingerTilstand,
  kanSkrive,
  opptatt,
  onOpprett,
  onOppdater,
  onAapne,
  onAvbryt,
  onTilbake,
  fraHypotese,
}: {
  modus: ProgramSkjemaModus;
  /** H3.4: skjemaet åpnet fra hypotesen. Bare i `ny`. */
  fraHypotese?: FraHypotese;
  produkter: Product[];
  /** Ukjent er strengest (ux-flow-architect): til produktene er lastet, kan ingenting lagres; feilet, sies det — aldri «ingen produkter». */
  produkterTilstand: 'laster' | 'feil' | 'ok';
  maksKontakterTak: number;
  minorsPresent: boolean;
  /** Ukjent er strengest: til innstillingene er lastet, er vilkårsfeltet låst — det defaulter aldri til det løsere. */
  innstillingerTilstand: 'laster' | 'feil' | 'ok';
  kanSkrive: boolean;
  opptatt: boolean;
  onOpprett: (body: ProgramCreate) => void;
  onOppdater: (id: string, body: ProgramUpdate) => void;
  onAapne: (id: string, body: ProgramUpdate) => void;
  onAvbryt: () => void;
  /** Under breakpointen (DD-51) tar skjemaet lista sin plass; «Tilbake til lista» står øverst. */
  onTilbake?: () => void;
}) {
  const program = modus.navn === 'rediger' ? modus.program : null;
  const laast = program !== null && program.status !== 'utkast';
  const smal = useSmal();
  const fh = program ? undefined : fraHypotese;
  const fylt = fh?.utfylling.verdier ?? {};
  const helSetning = fh?.utfylling.helSetning;
  const nrTekst = fh ? `H-${fh.nr}` : '';
  // Hypotesen har ikke noe produkt: med flere å velge mellom, velger hun selv (konseptets skjerm 4).
  const velgProduktSelv = fh !== undefined && produkter.length > 1;

  const [navn, setNavn] = useState(program?.name ?? fylt.navn ?? '');
  const [hypotese, setHypotese] = useState(program?.hypothesis ?? fylt.hypotese ?? '');
  const [kriterium, setKriterium] = useState(program?.successCriterion?.text ?? fylt.kriterium ?? '');
  const [maltall, setMaltall] = useState(program?.successCriterion?.target ?? fylt.maltall ?? '');
  const [fra, setFra] = useState(dato(program?.opensAt) || fylt.fra || dato(new Date().toISOString()));
  const [til, setTil] = useState(dato(program?.closesAt) || fylt.til || dato(new Date(Date.now() + 14 * 86_400_000).toISOString()));
  const [maksKontakter, setMaksKontakter] = useState(String(program?.maxContacts ?? maksKontakterTak));
  const [vilkar, setVilkar] = useState(program?.termsUrl ?? '');
  const [flagRef, setFlagRef] = useState(program?.flagRef ?? '');
  const [produktId, setProduktId] = useState(program?.productId ?? (velgProduktSelv ? '' : (produkter[0]?.id ?? '')));
  const [oppgaver, setOppgaver] = useState<Oppgaverad[]>(
    program
      ? program.tasks.map((t) => ({ title: t.title, instruction: t.instruction, screenHint: t.screenHint ?? '', estimatedMinutes: String(t.estimatedMinutes) }))
      : [{ ...STANDARD_OPPGAVE }],
  );
  const navnRef = useRef<HTMLInputElement | null>(null);
  const produktRef = useRef<HTMLSelectElement | null>(null);
  const hypoteseRef = useRef<HTMLTextAreaElement | null>(null);

  // Fokus der hun har noe å gjøre: kortversjonen (A6), så produktet hypotesen ikke sier, ellers navnet.
  useEffect(() => {
    if (helSetning) hypoteseRef.current?.focus();
    else if (velgProduktSelv) produktRef.current?.focus();
    else navnRef.current?.focus();
    // Bare ved åpning: fokus flyttes aldri mens hun skriver.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!produktId && produkter[0] && (!fh || produkter.length === 1)) setProduktId(produkter[0].id);
  }, [produkter, produktId, fh]);

  /** «fra H-142» til hun endrer feltet: verdien er fortsatt den som ble fylt ut (konseptets skjerm 4). */
  const merke = (felt: UtfyltFelt, verdi: string): ReactNode => {
    const fraFelt = fylt[felt];
    if (!fh || fraFelt === undefined || verdi !== fraFelt) return null;
    return (
      <span className="panel-prg-fra" data-testid={`prg-merke-${felt}`}>
        <Ikon navn="telescope-outline" />
        {felt === 'fra' || felt === 'til' ? 'fra testperioden' : `fra ${nrTekst}`}
      </span>
    );
  };
  const testerneSer = <span className="panel-prg-bi">· testerne ser dette</span>;

  const settOppgave = (i: number, patch: Partial<Oppgaverad>) => setOppgaver((xs) => xs.map((o, j) => (j === i ? { ...o, ...patch } : o)));
  const utenTid = oppgaver.filter((o) => !o.estimatedMinutes.trim() || Number(o.estimatedMinutes) < 1).length;
  // Aksepten viser SUMMEN (DD-32, konseptets «2 oppgaver · 15 min»): det testeren setter av tid til.
  const samletTid = oppgaver.reduce((a, o) => a + (Number(o.estimatedMinutes) || 0), 0);

  // Forklart lås (DD-17): manglene navngis i knappens grunn, ikke bare «låst».
  const mangler = useMemo(
    () =>
      [
        !hypotese.trim() && 'hypotese',
        !kriterium.trim() && 'suksesskriterium',
        oppgaver.length === 0 && 'minst én oppgave',
        oppgaver.length > 0 && oppgaver.some((o) => !o.title.trim() || !o.instruction.trim()) && 'tekst på hver oppgave',
        utenTid > 0 && `anslått tid på ${utenTid} oppgave${utenTid > 1 ? 'r' : ''}`,
      ].filter((x): x is string => typeof x === 'string'),
    [hypotese, kriterium, oppgaver, utenTid],
  );

  const periodeUgyldig = !fra || !til || new Date(til) <= new Date(fra);
  const navnTomt = navn.trim() === '';
  const lagreGrunn = !kanSkrive
    ? 'Rollen leser kan ikke opprette programmer'
    : navnTomt
      ? 'Funksjonen må ha et navn — i brukerens ord'
      : periodeUgyldig
        ? 'Sluttdato må være etter startdato'
        : !program && produkterTilstand === 'laster'
          ? 'Henter produkter …'
          : !program && produkterTilstand === 'feil'
            ? 'Kunne ikke hente produktene — prøv igjen'
            : !program && !produktId
              ? velgProduktSelv
                ? 'Velg produktet — hypotesen sier ikke hvilket.'
                : 'Ingen produkter — programmet må høre til et produkt'
              : innstillingerTilstand === 'laster'
                ? 'Henter innstillinger …'
                : innstillingerTilstand === 'feil'
                  ? 'Kunne ikke hente innstillingene — prøv igjen'
                  : opptatt
                    ? 'Lagrer …'
                    : null;
  // Vilkårsfeltet er låst til innstillingene er kjent: ukjent er strengest, aldri det løsere.
  const vilkarLaast = minorsPresent || innstillingerTilstand !== 'ok';
  const vilkarGrunn = minorsPresent
    ? 'Låst fordi brukerne deres kan være mindreårige. Programmet er forpliktelsesfritt — ingen vilkår ut over «du kan gå ut når som helst».'
    : innstillingerTilstand === 'laster'
      ? 'Henter innstillinger …'
      : innstillingerTilstand === 'feil'
        ? 'Kunne ikke hente innstillingene — prøv igjen'
        : null;

  const aapneGrunn =
    lagreGrunn ??
    (!program
      ? fh && !hypotese.trim()
        ? 'Kan åpnes når hypotesen er skrevet og utkastet er lagret.'
        : 'Lagre utkastet først — det kan åpnes når hypotesen og «Vi har rett hvis» er fylt inn.'
      : laast
        ? 'Programmet er alt åpnet'
        : mangler.length
          ? `Kan ikke åpnes: ${mangler.join(', ')} mangler. Et program uten suksesskriterium er ikke et program, og en oppgave uten tid er et løfte testeren ikke kan vurdere.`
          : null);

  const felles = () => ({
    name: navn.trim(),
    hypothesis: hypotese.trim() || undefined,
    successCriterion: kriterium.trim() ? { text: kriterium.trim(), ...(maltall.trim() ? { target: maltall.trim() } : {}) } : undefined,
    flagRef: flagRef.trim() || undefined,
    opensAt: new Date(`${fra}T00:00:00Z`).toISOString(),
    closesAt: new Date(`${til}T23:59:00Z`).toISOString(),
    maxContacts: Number(maksKontakter),
    ...(minorsPresent || !vilkar.trim() ? {} : { termsUrl: vilkar.trim() }),
    tasks: oppgaver.map((o) => ({
      title: o.title.trim(),
      instruction: o.instruction.trim(),
      ...(o.screenHint.trim() ? { screenHint: o.screenHint.trim() } : {}),
      estimatedMinutes: Number(o.estimatedMinutes) || 0,
    })),
  });

  const lagre = () => {
    if (lagreGrunn) return;
    if (program) onOppdater(program.id, felles() as ProgramUpdate);
    else onOpprett({ ...felles(), productId: produktId } as ProgramCreate);
  };

  const aapne = () => {
    if (aapneGrunn || !program) return;
    onAapne(program.id, { ...felles(), status: 'aapen' } as ProgramUpdate);
  };

  // Ctrl+Enter lagrer, som i saksdetaljen (DD-11): samme tast, samme betydning — «behold det
  // jeg har skrevet» — også mens markøren står i et felt.
  const paaTast = (e: ReactKeyboardEvent<HTMLElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      lagre();
    }
  };

  return (
    <aside aria-labelledby="prg-skjema-h" className="panel-prg-skjema" data-testid="prg-skjema" onKeyDown={paaTast}>
      <div className="panel-prg-kropp">
        <div className="panel-prg-smule">
          {fh ? (
            // «‹ H-142» tar henne tilbake uten å lagre (konseptets skjerm 4 og 6).
            <span className="panel-prg-smule-fra">
              <Link to={fh.tilbake.til} state={fh.tilbake.state} className="panel-knapp-lenke panel-tilbake" aria-label={`Tilbake til ${nrTekst}`}>
                <Ikon navn="chevron-back-outline" /> {nrTekst}
              </Link>
              {smal ? null : <span>· Betaprogram · Nytt program</span>}
            </span>
          ) : onTilbake ? (
            <button type="button" className="panel-knapp-lenke panel-tilbake" onClick={onTilbake}>
              <Ikon navn="arrow-back-outline" /> Tilbake til lista
            </button>
          ) : (
            <span>{program ? 'Rediger' : 'Nytt program'}</span>
          )}
        </div>
        <h2 id="prg-skjema-h" className="panel-prg-tittel">
          {program ? `Rediger «${program.name}»` : 'Nytt program'}
        </h2>
        {fh ? (
          // Skjermens ene hjelpesetning: hvor feltene kom fra, og at ingenting er lagret.
          <p className="panel-prg-fra-setning" data-testid="prg-fra-setning">
            <Ikon navn="telescope-outline" />
            <span>
              Fylt ut fra <b>{smal ? nrTekst : `${nrTekst} «${fh.tittel}»`}</b> — ingenting er lagret før du lagrer.
            </span>
          </p>
        ) : null}

        {/* Elleve felt (konseptets skjerm 4). Hjelpetekst BARE der feltet ikke forklarer seg
            selv: to i hele skjemaet (F15). Reglene — «aldri heves» (DD-14), «lenke, aldri
            avkryssing» (DD-37) — bor i DD-ene og i «Om» under Innstillinger, ikke her. */}
        <div className="panel-prg-felter">
          {/* P1 (H3.4): navnet og hypotesen er tekst testerne ser i aksepten — etiketten sier det. */}
          <label className="panel-felt">
            <span className="panel-felt-navn panel-prg-etikett">
              <span>Funksjonen, i testerens ord {testerneSer}</span>
              {merke('navn', navn)}
            </span>
            <input ref={navnRef} type="text" maxLength={NAVN_MAKS} value={navn} onChange={(e) => setNavn(e.target.value)} data-testid="prg-navn" placeholder="Kopier forrige uke" />
          </label>

          {/* `productId` er obligatorisk i kontrakten; med ett produkt er valget tatt, og feltet skjules. */}
          {!program && produkter.length > 1 ? (
            <label className="panel-felt">
              <span className="panel-felt-navn">Produkt</span>
              <select ref={produktRef} value={produktId} onChange={(e) => setProduktId(e.target.value)} data-testid="prg-produkt">
                {velgProduktSelv ? <option value="">Velg produkt</option> : null}
                {produkter.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </label>
          ) : null}

          {/* Feltet er en div med etikett, ikke en label rundt alt: kildeflaten (A6) og telleren skal
              ikke bli en del av feltets navn — flaten er beskrivelsen. */}
          <div className="panel-felt panel-prg-bred">
            <span className="panel-felt-navn panel-prg-etikett">
              <label htmlFor="prg-hypotese-felt">Hypotese {testerneSer}</label>
              {merke('hypotese', hypotese)}
              {helSetning ? (
                <span className="panel-prg-teller" data-testid="prg-hypotese-teller">
                  {hypotese.length} / {HYPOTESE_MAKS}
                </span>
              ) : null}
            </span>
            {helSetning ? (
              // A6: aldri kuttet — hele setningen står over det tomme feltet, og et menneske skriver kortversjonen.
              <div id="prg-hel-setning" className="panel-prg-kilde" data-testid="prg-hel-setning">
                <span className="panel-prg-fra">
                  <Ikon navn="telescope-outline" />
                  Hele setningen fra {nrTekst} · {helSetning.length} tegn
                </span>
                <p className="panel-prg-kilde-tekst">{helSetning}</p>
              </div>
            ) : null}
            <textarea
              id="prg-hypotese-felt"
              ref={hypoteseRef}
              rows={helSetning ? 3 : 2}
              maxLength={HYPOTESE_MAKS}
              value={hypotese}
              onChange={(e) => setHypotese(e.target.value)}
              aria-describedby={helSetning ? 'prg-hel-setning' : undefined}
              data-testid="prg-hypotese"
              placeholder={helSetning ? 'Skriv en kortversjon. Hele setningen står over.' : 'Vi tror at …'}
            />
          </div>

          {/* DD-17: suksesskriteriet er to felt som leses som ÉN setning — «Vi har rett hvis … er …». */}
          <label className="panel-felt">
            <span className="panel-felt-navn panel-prg-etikett">
              <span>Vi har rett hvis</span>
              {merke('kriterium', kriterium)}
            </span>
            <textarea rows={2} maxLength={KRITERIUM_MAKS} value={kriterium} onChange={(e) => setKriterium(e.target.value)} data-testid="prg-kriterium" placeholder="median sekunder til avspilling" />
          </label>

          <label className="panel-felt">
            <span className="panel-felt-navn panel-prg-etikett">
              <span>er</span>
              {merke('maltall', maltall)}
            </span>
            <input type="text" maxLength={MALTALL_MAKS} value={maltall} onChange={(e) => setMaltall(e.target.value)} data-testid="prg-maltall" placeholder="under 4 s" />
          </label>

          <label className="panel-felt">
            <span className="panel-felt-navn panel-prg-etikett">
              <span>Fra</span>
              {merke('fra', fra)}
            </span>
            <input type="date" value={fra} onChange={(e) => setFra(e.target.value)} data-testid="prg-fra" />
          </label>
          <label className="panel-felt">
            <span className="panel-felt-navn panel-prg-etikett">
              <span>Til</span>
              {merke('til', til)}
            </span>
            <input type="date" value={til} onChange={(e) => setTil(e.target.value)} data-testid="prg-til" />
          </label>

          {/* Hjelpesetning 1 av 2: det testeren får vite. Taket håndheves av `max` og av serveren. */}
          <label className="panel-felt">
            <span className="panel-felt-navn">Maks kontakter</span>
            <input type="number" min={0} max={maksKontakterTak} value={maksKontakter} onChange={(e) => setMaksKontakter(e.target.value)} data-testid="prg-kontakter" />
            <span className="panel-prg-hjelp">Testeren ser tallet før hun sier ja.</span>
          </label>

          {/* Hjelpesetning 2 av 2: flagget er leietakerens, og `flagRef` styrer ingenting. */}
          <label className="panel-felt">
            {/* Kontraktens feltnavn står lite ved etiketten for den som leser kontrakten (DD-50, Bryter-mønsteret). */}
            <span className="panel-felt-navn">
              Flaggnavn hos dere <span className="panel-bryter-felt">flag_ref</span>
            </span>
            <input type="text" value={flagRef} onChange={(e) => setFlagRef(e.target.value)} data-testid="prg-flagref" placeholder="kopier-forrige-uke-v2" />
            <span className="panel-prg-hjelp">Dere slår flagget på selv.</span>
          </label>

          <label className="panel-felt panel-prg-bred">
            <span className="panel-felt-navn">
              Vilkår <span className="panel-prg-bi">· lenke, valgfritt</span>
            </span>
            <input type="url" value={minorsPresent ? '' : vilkar} onChange={(e) => setVilkar(e.target.value)} disabled={vilkarLaast} aria-describedby={vilkarGrunn ? 'prg-vilkar-grunn' : undefined} data-testid="prg-vilkar" placeholder={minorsPresent ? 'Finnes ikke i dette programmet' : 'https://…/vilkar'} />
            {vilkarGrunn ? (
              <span id="prg-vilkar-grunn" className="panel-grunn">
                <Ikon navn="lock-closed-outline" /> {vilkarGrunn}
              </span>
            ) : null}
          </label>
        </div>

        <fieldset className="panel-prg-oppgaver" disabled={laast}>
          <legend className="panel-felt-navn">
            Oppgaver <span className="panel-prg-bi">· 1 til {MAKS_OPPGAVER}</span>
          </legend>
          {laast ? <p className="panel-grunn">Låst: oppgavene kan ikke endres etter at programmet er åpnet — testerne har akseptert dem.</p> : null}
          <ol className="panel-prg-oppgaveliste">
            {oppgaver.map((o, i) => (
              <li key={i} className="panel-prg-oppgave">
                <span className="panel-prg-oppgave-nr">{i + 1}</span>
                <input type="text" maxLength={NAVN_MAKS} value={o.title} onChange={(e) => settOppgave(i, { title: e.target.value })} aria-label={`Oppgave ${i + 1}: tittel`} data-testid={`prg-oppgave-tittel-${i}`} placeholder="Kopier forrige ukes kvelder" />
                <textarea rows={2} maxLength={500} value={o.instruction} onChange={(e) => settOppgave(i, { instruction: e.target.value })} aria-label={`Oppgave ${i + 1}: instruksjon`} data-testid={`prg-oppgave-instruksjon-${i}`} placeholder="Hva testeren skal gjøre." />
                <input type="text" className="panel-prg-oppgave-skjerm" value={o.screenHint} onChange={(e) => settOppgave(i, { screenHint: e.target.value })} aria-label={`Oppgave ${i + 1}: skjerm`} data-testid={`prg-oppgave-skjerm-${i}`} placeholder="Skjerm (valgfritt)" />
                <input type="number" className="panel-prg-oppgave-min" min={1} max={120} value={o.estimatedMinutes} onChange={(e) => settOppgave(i, { estimatedMinutes: e.target.value })} aria-label={`Oppgave ${i + 1}: anslått tid i minutter`} data-testid={`prg-oppgave-min-${i}`} />
                {/* «Fjern» er NØYTRAL: den endrer bare et utkast, og rødt betyr «fjernes på serveren» (DD-53). */}
                <button type="button" className="panel-knapp-liten panel-prg-fjern" onClick={() => setOppgaver((xs) => xs.filter((_, j) => j !== i))} aria-label={`Fjern oppgave ${i + 1}`} data-testid={`prg-oppgave-fjern-${i}`}>
                  <Ikon navn="close-outline" />
                </button>
              </li>
            ))}
          </ol>
          <button type="button" className="panel-knapp-lenke" disabled={oppgaver.length >= MAKS_OPPGAVER} onClick={() => setOppgaver((xs) => [...xs, { title: '', instruction: '', screenHint: '', estimatedMinutes: '5' }])} data-testid="prg-oppgave-ny">
            <Ikon navn="add-outline" /> Oppgave
          </button>
        </fieldset>

        {/* AKSEPTEN som kvitteringskort (DD-13, DD-32): tallene testeren ser før hun sier ja.
            «N oppgaver · M min» er skjemaets ENE helt (DD-67). «Gå ut · når som helst» står
            alltid — det er det eneste som gjelder ved mindreårige brukere. */}
        <div className="panel-prg-aksept" role="note" aria-label="Aksepten testeren ser" data-testid="prg-aksept">
          <span className="panel-etikett">Slik ser testeren aksepten</span>
          {/* P1 (H3.4): navnet og hypotesen slik testeren får dem — der teksten havner, før hun lagrer. */}
          {navn.trim() ? <p className="panel-prg-aksept-navn">{navn.trim()}</p> : null}
          {hypotese.trim() ? (
            <p className="panel-prg-aksept-hypotese">{hypotese.trim()}</p>
          ) : helSetning ? (
            <p className="panel-prg-aksept-hypotese panel-prg-aksept-tom">Kortversjonen du skriver, står her.</p>
          ) : null}
          <p className="panel-prg-aksept-helt" data-testid="prg-forhandsvisning">
            {oppgaver.length} {oppgaver.length === 1 ? 'oppgave' : 'oppgaver'} · {samletTid} min
          </p>
          <div className="panel-prg-aksept-rutenett">
            <div>
              <span className="panel-etikett">Når</span>
              <span className="panel-prg-aksept-verdi">{periode(fra, til)}</span>
            </div>
            <div>
              <span className="panel-etikett">Kontakt</span>
              <span className="panel-prg-aksept-verdi">maks {maksKontakter || 0} ganger</span>
            </div>
            <div>
              <span className="panel-etikett">Gå ut</span>
              <span className="panel-prg-aksept-verdi">når som helst</span>
            </div>
          </div>
        </div>

        <Om tittel="lagring og åpning">
          <p>Ingenting lagres før «Lagre utkast», og et utkast er usynlig for alle andre enn dere.</p>
          <p>«Åpne for invitasjoner» tar programmet fra utkast til åpent. «Aktiv» settes først når dere bekrefter flagget for den første deltakeren — plattformen rører aldri flagget selv.</p>
          <p>Anslått tid er obligatorisk per oppgave: aksepten viser tallene, ikke bare rekkefølgen.</p>
          {minorsPresent ? <p>Brukerne deres kan være mindreårige, så programmet er forpliktelsesfritt: ingen vilkår, ingen insentiv, ingen frist.</p> : <p>Vilkår vises i aksepten som lenke, aldri som avkryssing som binder.</p>}
        </Om>
      </div>

      {/* FOTEN (DD-58, presisert P4): «Lagre utkast» er primærvalget, «Åpne for invitasjoner»
          er sperret til utkastet finnes og kriteriet står, og låsen FORKLARES (DD-17).
          Nøyaktig én primærknapp (vakt 7). «Lukk» lukker skjemaet; «Avbryt» er reservert for
          dialogens nei og «Avbryt programmet». */}
      <footer className="panel-prg-fot" data-testid="prg-skjema-fot">
        {fh?.feil ? (
          // Skjerm 20: svaret fra tjeneren i klientens ord, aldri rødt. Det hun skrev står.
          <Varsel handling={fh.feil.onProevIgjen ? <button type="button" className="panel-knapp-lenke" onClick={fh.feil.onProevIgjen}>Prøv igjen</button> : undefined}>{fh.feil.tekst}</Varsel>
        ) : null}
        <div className="panel-prg-knapper">
          <Knapp label={program ? 'Lagre' : 'Lagre utkast'} kbd="Ctrl+Enter" primaer={!program} onClick={lagre} grunn={lagreGrunn} testid="prg-lagre" />
          {/* Deler knappene sperregrunn, sies den ÉN gang og den andre peker på den (F29). Fra
              hypotesen på telefonen står «Åpne for invitasjoner» på programsiden etter lagring (skjerm 6). */}
          {fh && smal ? null : <Knapp label="Åpne for invitasjoner" primaer={Boolean(program)} onClick={aapne} grunn={aapneGrunn} {...(aapneGrunn === lagreGrunn ? { grunnId: 'grunn-Ctrl-Enter' } : {})} testid="prg-aapne" />}
          <Knapp label="Lukk" kbd="Esc" onClick={onAvbryt} testid="prg-lukk" />
        </div>
      </footer>
    </aside>
  );
}

/** ISO → `yyyy-mm-dd` for <input type="date">. Tom streng når datoen mangler. */
function dato(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}
